import { useEffect, useMemo, useState } from 'react';
import type { ContactInfo } from '../../stores/klanten-store';
import { PhoneNumber } from '../phone/PhoneNumber';

interface Props {
  isOpen: boolean;
  accountName: string;
  contacts: ContactInfo[];
  onSelect: (contact: ContactInfo) => void;
  onCancel: () => void;
  /** Override van de subtitel. Standaard: order-context. */
  subtitle?: string;
}

/**
 * Modal voor contactpersoon-selectie bij B2B-acties (orders, e-mail) met
 * ≥2 contactpersonen. Gebruikt de bestaande activeCustomer.contacts lijst —
 * geen API-call nodig.
 */
export function ContactPickerDialog({ isOpen, accountName, contacts, onSelect, onCancel, subtitle }: Props) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (isOpen) setQuery('');
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.phone.toLowerCase().includes(q)
    );
  }, [contacts, query]);

  if (!isOpen) return null;

  const showSearch = contacts.length > 5;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Kies contactpersoon</h2>
          <p className="text-xs text-slate-500 mt-1">
            {subtitle ?? (
              <>Voor wie binnen <span className="font-medium">{accountName}</span> is deze order?</>
            )}
          </p>
        </div>

        {showSearch && (
          <div className="px-6 pt-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Zoek op naam, e-mail of telefoon"
              autoFocus
              className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm"
            />
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">Geen contactpersonen gevonden.</p>
          ) : (
            filtered.map((contact) => (
              <button
                key={contact.id}
                type="button"
                onClick={() => onSelect(contact)}
                className="w-full text-left px-4 py-3 border border-slate-200 rounded-md hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-900">{contact.name}</span>
                  {contact.title && (
                    <span className="text-xs text-slate-500">{contact.title}</span>
                  )}
                </div>
                {(contact.email || contact.phone) && (
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                    {contact.email && <span>{contact.email}</span>}
                    {contact.phone && <PhoneNumber number={contact.phone} sourceContext="contact-picker" stopPropagation />}
                  </div>
                )}
                {contact.department && (
                  <div className="mt-0.5 text-xs text-slate-400">{contact.department}</div>
                )}
              </button>
            ))
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
  );
}
